import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { RoleBox } from '../RoleBox';
import { TextInput } from '../TextInput';
import * as yup from 'yup';
import { GmailStepInputs, GoogleProfileObj } from 'pages/register';
import { SubmitHandler, useForm } from 'react-hook-form';
import { BigButton } from '../Buttons/BigButton';
import Link from 'components/Link';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import Auth from 'helpers/auth';
import { useUser } from 'context/user.context';
import phoneIcon from 'assets/images/phone.svg';
import axios from 'axios';
import { useCampaign } from 'context/campaign.context';

export const GmailSignup = ({
  googleData,
}: {
  googleData: GoogleProfileObj;
}) => {
  const [role_id, setRole_id] = useState<1 | 2>(1);
  const router = useRouter();
  const [, , setUser] = useUser();
  const queryReferralCode = router.query.referral_code as string | undefined;

  const schema: yup.SchemaOf<GmailStepInputs> = yup.object().shape({
    referrer_code: yup
      .string()
      .notRequired()
      .matches(/^(POD|pod)[a-zA-Z0-9]{7}\s*$/, {
        message: 'Invalid referral code',
        excludeEmptyString: true,
      }),
    phone_no: yup
      .string()
      .matches(/^[0-9]{10}$/, 'Enter valid 10 digit mobile number')
      .required('Mobile no is required'),
  });

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<GmailStepInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      referrer_code: queryReferralCode || '',
    },
    mode: 'onTouched',
  });

  // const campaign = router.query.campaign as string | undefined;
  const campaign = useCampaign();

  const onSubmit: SubmitHandler<GmailStepInputs> = ({
    referrer_code,
    phone_no,
  }) => {
    const body = {
      ...googleData,
      referrer_code: referrer_code?.toUpperCase().trim(),
      role_id,
      phone_no: phone_no.trim(),
      type: 'signup',
    };

    axios
      .post(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/user/socialLogin`,
        body,
        {
          params: {
            campaign,
          },
        },
      )
      .then((res: any) => {
        const data = res.data.data;
        Auth.setAuthToken(data.token);
        if (data.user.role_id === 1) {
          setUser({ user: res.data?.data?.user, isLoading: false });
          router.push('/startups');
        } else if (data.user.role_id === 2) {
          setUser({ user: res.data?.data?.user, isLoading: false });
          router.push('/create-startup-profile');
        } else {
          toast.success('Admin cannot login', {
            autoClose: 5000,
            hideProgressBar: true,
          });
        }
      })
      .catch((err: any) => {
        toast.error(err.message, { autoClose: 5000, hideProgressBar: true });
        // setLoader(false);
      });
  };

  return (
    <Box>
      <ToastContainer />
      <Typography
        component="h1"
        mt="42px"
        color="#091E42"
        fontSize={28}
        fontWeight={700}
        lineHeight="32.81px"
      >
        Sign up
      </Typography>
      <Typography
        mt={1.5}
        fontSize={14}
        lineHeight="21px"
        color="rgba(0, 0, 0, 0.67)"
      >
        Join as
      </Typography>
      <Box display="flex" mt="9px" mb="19px">
        <RoleBox
          sx={{
            bgcolor: role_id == 1 ? '#008B5E' : undefined,
            color: role_id == 1 ? '#fff' : undefined,
          }}
          onClick={() => setRole_id(1)}
        >
          Investors
        </RoleBox>
        <RoleBox
          sx={{
            bgcolor: role_id == 2 ? '#008B5E' : undefined,
            color: role_id == 2 ? '#fff' : undefined,
          }}
          onClick={() => {
            setRole_id(2);
            setValue('referrer_code', '');
            clearErrors('referrer_code');
          }}
        >
          Startups
        </RoleBox>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          sx={{ mt: '2px' }}
          placeholder="Phone No."
          icon={phoneIcon.src}
          register={register('phone_no')}
          errorMessage={errors.phone_no?.message}
          maxLength={10}
        />
        {role_id == 1 && (
          <TextInput
            placeholder="Referral Code (Optional)"
            register={register('referrer_code')}
            errorMessage={errors.referrer_code?.message}
          />
        )}

        <BigButton
          onClick={handleSubmit(onSubmit)}
          type="submit"
          sx={{ py: '10px', width: '100%' }}
        >
          <Typography fontWeight={700} fontSize={18}>
            SIGN UP
          </Typography>
        </BigButton>
        <Typography mt="17px">
          Already have an account?{' '}
          <Link sx={{ fontWeight: 700 }} noLinkStyle href="/login">
            Login
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};
